import React, { type FC, memo, use, useCallback, useState } from "react";
import { Badge, Button, cn, Popover, ScrollShadow, Separator } from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Bell, CheckDouble, Xmark } from "@gravity-ui/icons";

import { ReleaseNotesContext } from "@domain/context/ReleaseNotesProvider";
import { UserContext } from "@domain/context/UserProvider";
import type { ReleaseNote } from "@domain/models/ReleaseNotes";
import { AppRoutes } from "@domain/routing/routeConstants";
import { ReleaseNoteMeta } from "@app/components/releaseNotes/ReleaseNoteBody";
import { ReleaseNoteModal } from "@app/components/releaseNotes/ReleaseNoteModal";

interface ItemProps {
    note: ReleaseNote;
    isUnread: boolean;
    onReadMore: (note: ReleaseNote) => void;
    onClear: (version: string) => void;
}

const FeedItem: FC<ItemProps> = memo(function FeedItem({ note, isUnread, onReadMore, onClear }) {
    const intl = useIntl();
    const handleReadMore = useCallback(() => onReadMore(note), [onReadMore, note]);
    const handleClear = useCallback(() => onClear(note.version), [onClear, note.version]);

    return (
        <li className="flex gap-2 py-2" data-testid={`release-note-item-${note.version}`}>
            <span
                aria-hidden
                className={cn("mt-1.5 size-2 shrink-0 rounded-full", isUnread ? "bg-accent" : "bg-transparent")}
            />
            <div className="min-w-0 flex-1">
                <div className={cn("text-sm", isUnread && "font-semibold")}>{note.title}</div>
                <ReleaseNoteMeta note={note} />
                <p className="mt-1 line-clamp-2 text-sm text-muted">{note.summary}</p>
                <Button size="sm" variant="ghost" className="mt-1 -ml-2" onPress={handleReadMore}>
                    <FormattedMessage id="app.releaseNotes.readMore" />
                </Button>
            </div>
            <Button
                isIconOnly
                size="sm"
                variant="ghost"
                aria-label={intl.formatMessage({ id: "app.releaseNotes.clear" })}
                onPress={handleClear}
            >
                <Xmark className="size-4" />
            </Button>
        </li>
    );
});

const ReleaseNotesBellImpl: FC = () => {
    const { feed, unreadCount, isRead, markRead, clear, markAllRead, clearAll } = use(ReleaseNotesContext);
    const isSignedIn = Boolean(use(UserContext).fireUser);
    const intl = useIntl();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [openNote, setOpenNote] = useState<ReleaseNote | null>(null);

    const handleReadMore = useCallback(
        (note: ReleaseNote) => {
            setIsOpen(false);
            setOpenNote(note);
            markRead(note.version);
        },
        [markRead],
    );
    const handleCloseModal = useCallback(() => setOpenNote(null), []);
    const handleShowAll = useCallback(() => {
        setIsOpen(false);
        navigate(AppRoutes.releaseNotes);
    }, [navigate]);

    const title = intl.formatMessage({ id: "app.releaseNotes.title" });

    if (!isSignedIn) {
        return null;
    }

    return (
        <>
            <Badge.Anchor className="mr-2">
                <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
                    <Button
                        isIconOnly
                        variant="outline"
                        className="bg-app-surface"
                        aria-label={title}
                        data-testid="header-release-notes"
                    >
                        <Bell className="size-4" />
                    </Button>
                    <Popover.Content placement="bottom end" className="w-96 max-w-[95vw]">
                        <Popover.Dialog aria-label={title} className="p-0">
                            <div className="flex items-center justify-between gap-2 px-4 py-2">
                                <Popover.Heading className="text-base font-semibold">{title}</Popover.Heading>
                                <div className="flex gap-1">
                                    <Button
                                        isIconOnly
                                        size="sm"
                                        variant="ghost"
                                        isDisabled={unreadCount === 0}
                                        aria-label={intl.formatMessage({ id: "app.releaseNotes.markAllRead" })}
                                        onPress={markAllRead}
                                    >
                                        <CheckDouble className="size-4" />
                                    </Button>
                                    <Button
                                        isIconOnly
                                        size="sm"
                                        variant="ghost"
                                        isDisabled={feed.length === 0}
                                        aria-label={intl.formatMessage({ id: "app.releaseNotes.clearAll" })}
                                        onPress={clearAll}
                                    >
                                        <Xmark className="size-4" />
                                    </Button>
                                </div>
                            </div>
                            <Separator />
                            {feed.length === 0 ? (
                                <p className="px-4 py-6 text-center text-sm text-muted">
                                    <FormattedMessage id="app.releaseNotes.empty" />
                                </p>
                            ) : (
                                <ScrollShadow className="max-h-[60vh]">
                                    <ul className="divide-y divide-default/40 px-4">
                                        {feed.map((note) => (
                                            <FeedItem
                                                key={note.version}
                                                note={note}
                                                isUnread={!isRead(note)}
                                                onReadMore={handleReadMore}
                                                onClear={clear}
                                            />
                                        ))}
                                    </ul>
                                </ScrollShadow>
                            )}
                            <Separator />
                            <div className="px-2 py-1">
                                <Button variant="ghost" size="sm" className="w-full" onPress={handleShowAll}>
                                    <FormattedMessage id="app.releaseNotes.showAll" />
                                </Button>
                            </div>
                        </Popover.Dialog>
                    </Popover.Content>
                </Popover>
                {unreadCount > 0 && (
                    <Badge
                        size="sm"
                        color="danger"
                        aria-label={intl.formatMessage({ id: "app.releaseNotes.unread" }, { count: unreadCount })}
                    >
                        <Badge.Label>{unreadCount}</Badge.Label>
                    </Badge>
                )}
            </Badge.Anchor>
            <ReleaseNoteModal note={openNote} onClose={handleCloseModal} />
        </>
    );
};

export const ReleaseNotesBell = memo(ReleaseNotesBellImpl);
